import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const getWeekNo = (date = new Date()) => {
  const d = new Date(date);
  d.setHours(0,0,0,0);
  d.setDate(d.getDate() + 3 - (d.getDay()+6)%7);
  const week1 = new Date(d.getFullYear(),0,4);
  return 1 + Math.round(((d-week1)/86400000 - 3 + (week1.getDay()+6)%7)/7);
};

const btn = (bg='#2563eb',color='#fff') => ({
  padding:'8px 16px', background:bg, color, border:'none', borderRadius:'8px',
  cursor:'pointer', fontSize:'13px', fontWeight:'600', display:'inline-flex',
  alignItems:'center', gap:'6px'
});
const inp = { width:'100%', padding:'9px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'13px', boxSizing:'border-box' };
const card = { background:'#fff', borderRadius:'12px', padding:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.08)', marginBottom:'16px' };

export default function WeeklyReports() {
  const { user } = useAuth();
  const isTech = ['subdist_tech_own','subdist_tech_isp','dist_l1'].includes(user?.role);
  const isAdmin = ['subdist_admin','dist_admin','dist_head','tenant_admin','superadmin','director','isp_dist_head'].includes(user?.role);
  const isSubDistAdmin = user?.role === 'subdist_admin';

  const [tab, setTab] = useState(isTech ? 'upload' : 'reports');
  const [expandedReports, setExpandedReports] = useState({});
  const toggleReport = (id) => setExpandedReports(prev=>({...prev,[id]:!prev[id]}));
  const [expandedSubDists, setExpandedSubDists] = useState({});
  const toggleSubDist = (sd) => setExpandedSubDists(prev=>({...prev,[sd]:!prev[sd]}));
  const [expandedAiItems, setExpandedAiItems] = useState({});
  const toggleAiItem = (id) => setExpandedAiItems(prev=>({...prev,[id]:!prev[id]}));
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState('');

  // Filters
  const [filterSub, setFilterSub] = useState('');
  const [filterWeek, setFilterWeek] = useState('');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [subdists, setSubdists] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterTech, setFilterTech] = useState('');
  const [techList, setTechList] = useState([]);

  const toast = (m, type='success') => { setMsg(m); setMsgType(type); setTimeout(()=>setMsg(''),4000); };

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if(filterSub) params.sub_dist = filterSub;
      if(viewMode==='month' && filterMonth) params.month = filterMonth;
      else if(filterWeek) params.week_no = filterWeek;
      if(filterYear) params.year = filterYear;
      if(filterType) params.device_type = filterType;
      if(filterTech) params.tech_id = filterTech;
      const r = await api.get(`/weekly/reports`, { params });
      setReports(r.data.reports || []);
    } catch(e) { toast('Failed to load reports','error'); }
    setLoading(false);
  }, [filterSub, filterWeek, filterYear, filterType, filterTech]);

  const loadStatus = useCallback(async () => {
    try {
      const params = { week_no:filterWeek, year:filterYear };
      if(filterSub) params.sub_dist = filterSub;
      const r = await api.get('/weekly/submission-status', { params });
      console.log('Status loaded:', r.data.status?.length);
      setStatus(r.data.status || []);
    } catch(e) { 
      console.error('Status error:', e);
      toast('Failed to load status','error'); 
    }
  }, [filterWeek, filterYear, filterSub]);

  const [aiTextLoading, setAiTextLoading] = useState(false);

  const loadAiData = useCallback(async () => {
    setAiLoading(true); setAiData(null); setAiText('');
    try {
      const params = { week_no: filterWeek, year: filterYear };
      if(filterSub) params.sub_dist = filterSub;
      const r = await api.get('/weekly/ai-analysis', { params });
      setAiData(r.data);
      setAiLoading(false);
      // Run AI analysis separately - don't block UI
      setAiTextLoading(true);
      await runAiAnalysis(r.data);
      setAiTextLoading(false);
    } catch(e) { toast('Failed to load analysis data','error'); setAiLoading(false); setAiTextLoading(false); }
  }, [filterWeek, filterYear, filterSub]);

  const runAiAnalysis = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch('/api/weekly/ai-weekly-report', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify({
          week_no: data.week_no,
          year: data.year,
          olt_reports: data.olt_reports||[],
          switch_reports: data.switch_reports||[],
          sub_dists: data.sub_dists||[]
        })
      });
      const d = await resp.json();
      if(d.success){
        // Strip any thinking tags that leaked through
        let txt = d.analysis||'';
        const thinkEnd = txt.lastIndexOf('</think>');
        if(thinkEnd !== -1) txt = txt.slice(thinkEnd+8).trim();
        setAiText(txt);
      }
      else setAiText('AI analysis failed: ' + d.message);
    } catch(e) { setAiText('AI analysis failed: ' + e.message); }
  };

  useEffect(() => {
    // Load sub-dist list for admin
    if(isAdmin) {
      api.get('/weekly/subdists').then(r=>setSubdists(r.data.subdists||[])).catch(()=>{});
      api.get('/users/technicians').then(r=>setTechList(r.data.technicians||[])).catch(()=>{});
    }
  }, [isAdmin]);

  // No auto-load - user must click Generate
  // Only auto-load subdist list

  const handleGenerate = () => {
    if(tab==='my-reports') loadReports();
    else if(tab==='status') loadStatus();
    else if(tab==='ai') loadAiData();
  };

  const downloadTemplate = async (type) => {
    try {
      const r = await api.get(`/weekly/template/${type}`, { responseType:'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type==='olt'?'OLT':'Switch'}_Weekly_Report_Template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { toast('Download failed','error'); }
  };

  const handleUpload = async (type, file) => {
    if(!file) return;
    setUploading(true); setUploadResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post(`/weekly/upload/${type}`, form, {
        headers: { 'Content-Type':'multipart/form-data' }
      });
      setUploadResult(r.data);
      toast(r.data.message, 'success');
      loadReports();
    } catch(e) { toast(e.response?.data?.message||'Upload failed','error'); }
    setUploading(false);
  };

  const weekLabel = (w,y) => `Week ${w}, ${y}`;
  const currentWeek = getWeekNo();
  const currentYear = new Date().getFullYear();

  const currentWeekNo = getWeekNo();
  const lastWeekNo = currentWeekNo > 1 ? currentWeekNo - 1 : 52;
  const lastWeekYear = currentWeekNo > 1 ? currentYear : currentYear - 1;

  const FilterBar = ({ onFilter, showDateRange=false }) => (
    <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap', alignItems:'center' }}>
      {isAdmin && !isSubDistAdmin && (
        <select style={{ ...inp, width:'160px' }} value={filterSub} onChange={e=>setFilterSub(e.target.value)}>
          <option value="">All Sub-Dists</option>
          {subdists.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
      )}
      <div style={{ display:'flex', borderRadius:'8px', overflow:'hidden', border:'1px solid #e2e8f0' }}>
        <button onClick={()=>{setViewMode('week');setFilterWeek(currentWeekNo);}} style={{ padding:'8px 12px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', background:viewMode==='week'&&filterWeek===currentWeekNo?'#2563eb':'#f8fafc', color:viewMode==='week'&&filterWeek===currentWeekNo?'#fff':'#64748b' }}>This Week</button>
        <button onClick={()=>{setViewMode('week');setFilterWeek(lastWeekNo);}} style={{ padding:'8px 12px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', background:viewMode==='week'&&filterWeek===lastWeekNo?'#2563eb':'#f8fafc', color:viewMode==='week'&&filterWeek===lastWeekNo?'#fff':'#64748b' }}>Last Week</button>
        <button onClick={()=>setViewMode('month')} style={{ padding:'8px 12px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', background:viewMode==='month'?'#2563eb':'#f8fafc', color:viewMode==='month'?'#fff':'#64748b' }}>Month</button>
        {showDateRange && <button onClick={()=>setViewMode('date')} style={{ padding:'8px 12px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', background:viewMode==='date'?'#2563eb':'#f8fafc', color:viewMode==='date'?'#fff':'#64748b' }}>Date Range</button>}
      </div>
      {viewMode==='month' && (
        <select style={{ ...inp, width:'130px' }} value={filterMonth} onChange={e=>setFilterMonth(e.target.value)}>
          <option value="">-- Month --</option>
          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m,i)=>(
            <option key={i+1} value={i+1}>{m}</option>
          ))}
        </select>
      )}
      {viewMode==='date' && showDateRange && (
        <>
          <input type="date" style={{ ...inp, width:'140px' }} value={filterDateFrom} onChange={e=>setFilterDateFrom(e.target.value)} />
          <span style={{ color:'#64748b', fontSize:'12px' }}>to</span>
          <input type="date" style={{ ...inp, width:'140px' }} value={filterDateTo} onChange={e=>setFilterDateTo(e.target.value)} />
        </>
      )}
      <select style={{ ...inp, width:'90px' }} value={filterYear} onChange={e=>setFilterYear(e.target.value)}>
        {[2025,2026,2027].map(y=><option key={y} value={y}>{y}</option>)}
      </select>
      {isAdmin && techList.length>0 && (
        <select style={{ ...inp, width:'160px' }} value={filterTech} onChange={e=>setFilterTech(e.target.value)}>
          <option value="">All Technicians</option>
          {techList.map(t=><option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      )}
      <button style={btn()} onClick={onFilter}>🔍 Generate</button>
    </div>
  );

  return (
    <div style={{ padding:'24px', maxWidth:'1100px', margin:'0 auto' }}>
      {msg && (
        <div style={{ position:'fixed', top:'20px', right:'20px', background:msgType==='success'?'#15803d':'#dc2626', color:'#fff', padding:'10px 18px', borderRadius:'8px', zIndex:9999, boxShadow:'0 4px 12px rgba(0,0,0,0.2)' }}>
          {msgType==='success'?'✅':'❌'} {msg}
        </div>
      )}

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
        <div>
          <h2 style={{ margin:0, fontSize:'20px', fontWeight:'700' }}>📅 Weekly Network Reports</h2>
          <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>
            Current: {weekLabel(currentWeek, currentYear)} • {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'short'})}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'4px', marginBottom:'20px', borderBottom:'2px solid #e2e8f0' }}>
        {isTech && [
          ['upload','📤 Upload Report'],
          ['my-reports','📋 My Reports'],
        ].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{ padding:'9px 16px', border:'none', background:'none', cursor:'pointer', fontWeight:tab===t?'700':'400', color:tab===t?'#2563eb':'#64748b', borderBottom:tab===t?'2px solid #2563eb':'2px solid transparent', marginBottom:'-2px', fontSize:'13px' }}>{l}</button>
        ))}
        {isAdmin && [
          ['reports','📊 All Reports'],
          ['ai','🤖 AI Analysis'],
          ['status','✅ Submission Status'],
        ].map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t)} style={{ padding:'9px 16px', border:'none', background:'none', cursor:'pointer', fontWeight:tab===t?'700':'400', color:tab===t?'#2563eb':'#64748b', borderBottom:tab===t?'2px solid #2563eb':'2px solid transparent', marginBottom:'-2px', fontSize:'13px' }}>{l}</button>
        ))}
      </div>

      {/* UPLOAD TAB */}
      {tab==='upload' && isTech && (
        <div>
          {/* OLT Upload */}
          <div style={card}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
              <div>
                <div style={{ fontWeight:'700', fontSize:'15px' }}>📡 OLT Weekly Report</div>
                <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>Upload active ONT counts per PON port for each OLT</div>
              </div>
              <button style={btn('#f0fdf4','#15803d')} onClick={()=>downloadTemplate('olt')}>
                ⬇️ Download Template
              </button>
            </div>
            <div style={{ border:'2px dashed #bfdbfe', borderRadius:'10px', padding:'24px', textAlign:'center', background:'#eff6ff' }}>
              <div style={{ fontSize:'32px', marginBottom:'8px' }}>📊</div>
              <div style={{ fontWeight:'600', fontSize:'13px', marginBottom:'4px' }}>Drop your filled OLT template here</div>
              <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'12px' }}>or click to browse — .xlsx files only</div>
              <input type="file" accept=".xlsx,.xls" id="olt-upload" style={{ display:'none' }}
                onChange={e=>handleUpload('olt', e.target.files[0])} />
              <label htmlFor="olt-upload" style={{ ...btn(), cursor:'pointer' }}>
                {uploading ? '⏳ Uploading...' : '📤 Select OLT File'}
              </label>
            </div>
          </div>

          {/* Switch Upload */}
          <div style={card}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
              <div>
                <div style={{ fontWeight:'700', fontSize:'15px' }}>🔀 Switch Weekly Report</div>
                <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>Upload traffic and error data for each switch</div>
              </div>
              <button style={btn('#f0fdf4','#15803d')} onClick={()=>downloadTemplate('switch')}>
                ⬇️ Download Template
              </button>
            </div>
            <div style={{ border:'2px dashed #bbf7d0', borderRadius:'10px', padding:'24px', textAlign:'center', background:'#f0fdf4' }}>
              <div style={{ fontSize:'32px', marginBottom:'8px' }}>📈</div>
              <div style={{ fontWeight:'600', fontSize:'13px', marginBottom:'4px' }}>Drop your filled Switch template here</div>
              <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'12px' }}>or click to browse — .xlsx files only</div>
              <input type="file" accept=".xlsx,.xls" id="sw-upload" style={{ display:'none' }}
                onChange={e=>handleUpload('switch', e.target.files[0])} />
              <label htmlFor="sw-upload" style={{ ...btn('#15803d'), cursor:'pointer' }}>
                {uploading ? '⏳ Uploading...' : '📤 Select Switch File'}
              </label>
            </div>
          </div>

          {/* Upload Result */}
          {uploadResult && (
            <div style={{ ...card, background: uploadResult.errors?.length?'#fef2f2':'#f0fdf4', border:`1px solid ${uploadResult.errors?.length?'#fca5a5':'#86efac'}` }}>
              <div style={{ fontWeight:'700', fontSize:'14px', marginBottom:'8px' }}>
                {uploadResult.errors?.length ? '⚠️' : '✅'} Upload Result
              </div>
              <div style={{ fontSize:'13px' }}>✅ Saved: <b>{uploadResult.saved}</b> records</div>
              {uploadResult.skipped>0 && <div style={{ fontSize:'13px', color:'#64748b' }}>⏭️ Skipped: {uploadResult.skipped} empty rows</div>}
              {uploadResult.errors?.length>0 && (
                <div style={{ marginTop:'8px' }}>
                  <div style={{ fontSize:'12px', fontWeight:'600', color:'#dc2626' }}>Errors:</div>
                  {uploadResult.errors.map((e,i)=><div key={i} style={{ fontSize:'11px', color:'#dc2626' }}>• {e}</div>)}
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          <div style={{ ...card, background:'#fffbeb', border:'1px solid #fde68a' }}>
            <div style={{ fontWeight:'700', fontSize:'13px', marginBottom:'10px' }}>📋 How to Submit Weekly Report</div>
            {[
              ['1️⃣', 'Download the OLT or Switch template using the button above'],
              ['2️⃣', 'Fill in your device details — Name, IP, Location (first time only)'],
              ['3️⃣', 'Enter this week\'s data — Active ONT counts / Traffic / Errors'],
              ['4️⃣', 'Save the Excel file and upload it here'],
              ['5️⃣', 'Devices are auto-registered on first upload — just update data each week'],
            ].map(([n,t])=>(
              <div key={n} style={{ display:'flex', gap:'8px', marginBottom:'6px', fontSize:'12px' }}>
                <span>{n}</span><span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MY REPORTS TAB */}
      {tab==='my-reports' && (
        <div>
          <div style={{ display:'flex', gap:'8px', marginBottom:'16px', alignItems:'center', flexWrap:'wrap' }}>
            <input type='date' style={{ ...inp, width:'150px' }} onChange={e=>{
              const d=new Date(e.target.value);
              const w=getWeekNo(d);
              setFilterWeek(w);
              setFilterYear(d.getFullYear());
            }} />
            <select style={{ ...inp, width:'120px' }} value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option value="olt">OLT</option>
              <option value="switch">Switch</option>
            </select>
            <button style={btn()} onClick={loadReports}>🔍 Filter</button>
          </div>
          {loading ? <div style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>Loading...</div> : renderReportsTable(reports, false)}
        </div>
      )}

      {/* AI ANALYSIS TAB */}
      {tab==='ai' && isAdmin && (
        <div>
          <FilterBar onFilter={loadAiData} showDateRange={false} />

          {aiLoading && (
            <div style={{ textAlign:'center', padding:'40px' }}>
              <div style={{ fontSize:'32px', marginBottom:'12px' }}>🤖</div>
              <div style={{ fontWeight:'600', color:'#2563eb' }}>AI is analyzing your network data...</div>
              <div style={{ fontSize:'12px', color:'#64748b', marginTop:'4px' }}>Comparing Week {filterWeek} vs Week {filterWeek>1?filterWeek-1:52}</div>
            </div>
          )}

          {aiData && !aiLoading && (
            <div>
              {/* Summary Stats */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }}>
                {[
                  { label:'OLTs Analyzed', val:aiData.olt_reports?.length||0, color:'#2563eb', bg:'#eff6ff' },
                  { label:'Switches Analyzed', val:aiData.switch_reports?.length||0, color:'#15803d', bg:'#f0fdf4' },
                  { label:'High Load PONs', val:(aiData.olt_reports||[]).reduce((s,r)=>s+r.high_load_pons,0), color:'#dc2626', bg:'#fef2f2' },
                  { label:'Sub-Dists', val:aiData.sub_dists?.length||0, color:'#7c3aed', bg:'#f5f3ff' },
                ].map(s=>(
                  <div key={s.label} style={{ background:s.bg, borderRadius:'10px', padding:'14px', textAlign:'center' }}>
                    <div style={{ fontSize:'24px', fontWeight:'800', color:s.color }}>{s.val}</div>
                    <div style={{ fontSize:'11px', color:'#64748b' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* Week comparison header */}
              <div style={{ background:'#1e293b', borderRadius:'10px', padding:'14px 18px', marginBottom:'16px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ color:'#fff', fontWeight:'700' }}>📊 Week {aiData.week_no}, {aiData.year}</div>
                <div style={{ color:'#94a3b8', fontSize:'12px' }}>vs</div>
                <div style={{ color:'#94a3b8', fontWeight:'600' }}>Week {aiData.prev_week}, {aiData.prev_year}</div>
              </div>

              {/* Network Data — Grouped by Sub-District, Collapsible */}
              {aiData.sub_dists?.length>0 && (
                <div style={{ marginBottom:'16px' }}>
                  <div style={{ fontWeight:'700', fontSize:'12px', color:'#475569', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                    📊 Network Data by Sub-District <span style={{ fontWeight:'400', color:'#64748b' }}>— Click to expand</span>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                    {aiData.sub_dists.map(sd=>{
                      const sdOlts = (aiData.olt_reports||[]).filter(r=>r.sub_dist===sd);
                      const sdSws = (aiData.switch_reports||[]).filter(r=>r.sub_dist===sd);
                      const sdCrit = sdOlts.reduce((s,r)=>s+r.critical_pons,0);
                      const sdHigh = sdOlts.reduce((s,r)=>s+r.high_load_pons,0);
                      const sdErr = sdSws.reduce((s,r)=>s+r.error_port_count,0);
                      const isExpSd = expandedAiItems['sd_'+sd];
                      const health = sdCrit>0?'🔴':sdHigh>0||sdErr>0?'🟡':'✅';
                      const bc = sdCrit>0?'#fca5a5':sdHigh>0||sdErr>0?'#fcd34d':'#e2e8f0';
                      const hbg = sdCrit>0?'#fef2f2':sdHigh>0||sdErr>0?'#fffbeb':'#f8fafc';
                      return (
                        <div key={sd} style={{ background:'white', borderRadius:'10px', border:`1px solid ${bc}`, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.05)' }}>
                          {/* Sub-District Header */}
                          <div onClick={()=>toggleAiItem('sd_'+sd)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', cursor:'pointer', background:hbg, userSelect:'none' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'12px', flex:1 }}>
                              <span style={{ fontSize:'18px' }}>{health}</span>
                              <div>
                                <div style={{ fontWeight:'800', fontSize:'14px', color:'#1e293b' }}>🏘️ {sd}</div>
                                <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px', display:'flex', gap:'10px', flexWrap:'wrap' }}>
                                  <span>📡 {sdOlts.length} OLTs</span>
                                  <span>🔀 {sdSws.length} Switches</span>
                                  {sdCrit>0&&<span style={{ color:'#dc2626', fontWeight:'700' }}>🚨 {sdCrit} Critical PONs</span>}
                                  {sdHigh>0&&<span style={{ color:'#d97706', fontWeight:'700' }}>⚠️ {sdHigh} High PONs</span>}
                                  {sdErr>0&&<span style={{ color:'#dc2626', fontWeight:'700' }}>❌ {sdErr} SW Errors</span>}
                                </div>
                              </div>
                            </div>
                            <span style={{ fontSize:'14px', color:'#94a3b8', transform:isExpSd?'rotate(180deg)':'rotate(0deg)', display:'inline-block', transition:'transform .2s' }}>▼</span>
                          </div>
                          {/* Expanded: OLTs + Switches */}
                          {isExpSd && (
                            <div style={{ padding:'12px', borderTop:`1px solid ${bc}`, background:'#fafafa', display:'flex', flexDirection:'column', gap:'6px' }}>
                              {/* OLTs */}
                              {sdOlts.map((r,ri)=>{
                                const isCrit=r.critical_pons>0; const isHigh=r.high_load_pons>0;
                                const isExp=expandedAiItems['olt_'+sd+'_'+ri];
                                const rHealth=isCrit?'🔴':isHigh?'🟡':'✅';
                                const rBc=isCrit?'#fca5a5':isHigh?'#fcd34d':'#e2e8f0';
                                const rBg=isCrit?'#fef2f2':isHigh?'#fffbeb':'white';
                                return (
                                  <div key={ri} style={{ background:'white', borderRadius:'8px', border:`1px solid ${rBc}`, overflow:'hidden' }}>
                                    <div onClick={()=>toggleAiItem('olt_'+sd+'_'+ri)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 12px', cursor:'pointer', background:rBg, userSelect:'none' }}>
                                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
                                        <span>{rHealth}</span>
                                        <div style={{ flex:1 }}>
                                          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                                            <span style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>📡 {r.name}</span>
                                            {r.lco_code&&<span style={{ fontSize:'10px', background:'#dbeafe', color:'#1d4ed8', padding:'1px 6px', borderRadius:'20px', fontWeight:'600' }}>{r.lco_code}</span>}
                                            {r.olt_vendor&&<span style={{ fontSize:'10px', background:'#f5f3ff', color:'#7c3aed', padding:'1px 6px', borderRadius:'20px' }}>{r.olt_vendor}</span>}
                                          </div>
                                          <div style={{ display:'flex', gap:'8px', marginTop:'2px', flexWrap:'wrap' }}>
                                            <span style={{ fontSize:'10px', color:'#15803d' }}>🟢 {r.total_online||0}</span>
                                            <span style={{ fontSize:'10px', color:'#dc2626' }}>🔴 {r.total_offline||0}</span>
                                            {r.high_load_pons>0&&<span style={{ fontSize:'10px', color:'#d97706', fontWeight:'700' }}>⚠️ {r.high_load_pons} High</span>}
                                            {r.critical_pons>0&&<span style={{ fontSize:'10px', color:'#dc2626', fontWeight:'700' }}>🚨 {r.critical_pons} Critical</span>}
                                          </div>
                                        </div>
                                      </div>
                                      <span style={{ fontSize:'12px', color:'#94a3b8', transform:isExp?'rotate(180deg)':'rotate(0deg)', display:'inline-block', transition:'transform .2s' }}>▼</span>
                                    </div>
                                    {isExp && (
                                      <div style={{ padding:'10px 12px', borderTop:`1px solid ${rBc}`, background:'#fafafa' }}>
                                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(68px,1fr))', gap:'4px' }}>
                                          {r.pon_trends?.filter(p=>p.online>0||p.total>0).map((p,pi)=>(
                                            <div key={pi} style={{ padding:'6px', borderRadius:'7px', textAlign:'center',
                                              background:p.online>=120?'#fef2f2':p.online>=100?'#fff7ed':'#f0fdf4',
                                              border:`2px solid ${p.online>=120?'#ef4444':p.online>=100?'#f97316':'#86efac'}` }}>
                                              <div style={{ fontSize:'9px', color:'#64748b', fontWeight:'600' }}>PON {p.pon}</div>
                                              <div style={{ fontSize:'16px', fontWeight:'800', color:p.online>=120?'#dc2626':p.online>=100?'#d97706':'#15803d' }}>{p.online}</div>
                                              <div style={{ fontSize:'8px', color:'#94a3b8' }}>/{p.total||0}</div>
                                              {p.diff!==null&&<div style={{ fontSize:'9px', fontWeight:'700', color:p.diff>0?'#dc2626':p.diff<0?'#15803d':'#94a3b8' }}>{p.diff>0?'↑':p.diff<0?'↓':'='}{Math.abs(p.diff)}</div>}
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {/* Switches */}
                              {sdSws.map((r,i)=>{
                                const hasErr=r.error_port_count>0; const hasWarn=r.high_mac_ports>0||r.low_rx_ports?.length>0;
                                const isExp=expandedAiItems['sw_'+sd+'_'+i];
                                const rHealth=hasErr?'🔴':hasWarn?'🟡':'✅';
                                const rBc=hasErr?'#fca5a5':hasWarn?'#fcd34d':'#e2e8f0';
                                const rBg=hasErr?'#fef2f2':hasWarn?'#fffbeb':'white';
                                return (
                                  <div key={i} style={{ background:'white', borderRadius:'8px', border:`1px solid ${rBc}`, overflow:'hidden' }}>
                                    <div onClick={()=>toggleAiItem('sw_'+sd+'_'+i)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 12px', cursor:'pointer', background:rBg, userSelect:'none' }}>
                                      <div style={{ display:'flex', alignItems:'center', gap:'8px', flex:1 }}>
                                        <span>{rHealth}</span>
                                        <div style={{ flex:1 }}>
                                          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                                            <span style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>🔀 {r.name}</span>
                                            {r.ip&&<span style={{ fontSize:'10px', color:'#94a3b8', fontFamily:'monospace' }}>{r.ip}</span>}
                                          </div>
                                          <div style={{ display:'flex', gap:'8px', marginTop:'2px' }}>
                                            <span style={{ fontSize:'10px', color:'#64748b' }}>🔌 {r.total_ports} ports</span>
                                            {r.error_port_count>0&&<span style={{ fontSize:'10px', color:'#dc2626', fontWeight:'700' }}>❌ {r.error_port_count} errors</span>}
                                          </div>
                                        </div>
                                      </div>
                                      <span style={{ fontSize:'12px', color:'#94a3b8', transform:isExp?'rotate(180deg)':'rotate(0deg)', display:'inline-block', transition:'transform .2s' }}>▼</span>
                                    </div>
                                    {isExp && (
                                      <div style={{ padding:'10px 12px', borderTop:`1px solid ${rBc}`, background:'#fafafa' }}>
                                        {r.error_ports_detail?.length>0&&(
                                          <div style={{ marginBottom:'6px' }}>
                                            <div style={{ fontSize:'11px', fontWeight:'700', color:'#dc2626', marginBottom:'3px' }}>❌ Error Ports:</div>
                                            <div style={{ display:'flex', flexWrap:'wrap', gap:'3px' }}>
                                              {r.error_ports_detail.map((p,pi)=>(
                                                <span key={pi} style={{ fontSize:'10px', background:'#fef2f2', color:'#dc2626', padding:'2px 7px', borderRadius:'8px', border:'1px solid #fca5a5' }}>
                                                  {p.port}{p.name&&` (${p.name})`} err:{p.errors}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                        {r.low_rx_ports?.length>0&&(
                                          <div>
                                            <div style={{ fontSize:'11px', fontWeight:'700', color:'#7c3aed', marginBottom:'3px' }}>📶 Low RX:</div>
                                            <div style={{ display:'flex', flexWrap:'wrap', gap:'3px' }}>
                                              {r.low_rx_ports.map((p,pi)=>(
                                                <span key={pi} style={{ fontSize:'10px', background:'#f5f3ff', color:'#7c3aed', padding:'2px 7px', borderRadius:'8px', border:'1px solid #c4b5fd' }}>
                                                  {p.port}{p.name&&` (${p.name})`} {p.dbm_rx}dBm
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* AI Text Analysis — Professional Report */}
              {aiText && (
                <div style={{ background:'white', borderRadius:'12px', border:'1px solid #e2e8f0', overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.08)' }}>
                  {/* Report Header */}
                  <div style={{ background:'linear-gradient(135deg,#1e293b,#334155)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ color:'#94a3b8', fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'1px', marginBottom:'3px' }}>AI Network Intelligence Report</div>
                      <div style={{ color:'white', fontSize:'15px', fontWeight:'800' }}>📡 Week {aiData?.week_no}, {aiData?.year} — Network Health Analysis</div>
                      <div style={{ color:'#94a3b8', fontSize:'11px', marginTop:'2px' }}>Powered by Groq AI • Generated {new Date().toLocaleString('en-IN',{dateStyle:'medium',timeStyle:'short'})}</div>
                    </div>
                    <button onClick={()=>{
                      const w=window.open('','_blank');
                      const content = aiText.split('\n').map(line=>{
                        if(line.match(/^[0-9]+\.|^#{1,3} |^[A-Z ]{5,}:$/)) return '<h3 style="color:#1e293b;margin:16px 0 6px;font-size:14px">'+line+'</h3>';
                        if(line.startsWith('- ')) return '<li style="margin:4px 0;color:#334155">'+line.slice(2)+'</li>';
                        if(line.trim()==='') return '<br>';
                        return '<p style="margin:4px 0;color:#334155;line-height:1.6">'+line+'</p>';
                      }).join('');
                      w.document.write(`<!DOCTYPE html><html><head><title>Weekly AI Network Report - Week ${aiData?.week_no}, ${aiData?.year}</title>
                        <style>
                          body{font-family:'Arial',sans-serif;padding:0;margin:0;color:#1e293b}
                          .header{background:linear-gradient(135deg,#1e293b,#334155);color:white;padding:24px 32px}
                          .header h1{margin:0 0 4px;font-size:20px}
                          .header p{margin:0;opacity:.7;font-size:12px}
                          .content{padding:24px 32px}
                          h3{color:#1e293b;border-bottom:1px solid #e2e8f0;padding-bottom:6px}
                          li{margin:4px 0}
                          .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}
                          @media print{body{print-color-adjust:exact}}
                        </style></head>
                        <body>
                        <div class="header">
                          <h1>📡 Weekly Network Intelligence Report</h1>
                          <p>Week ${aiData?.week_no}, ${aiData?.year} • PDIC Network Operations • Generated ${new Date().toLocaleString('en-IN')}</p>
                        </div>
                        <div class="content">${content}</div>
                        <div class="content"><div class="footer">PDIC Network Management System • Powered by Groq AI</div></div>
                        </body></html>`);
                      w.document.close();
                      setTimeout(()=>w.print(),500);
                    }} style={{ background:'#2563eb', color:'white', border:'none', borderRadius:'8px', padding:'8px 16px', cursor:'pointer', fontSize:'12px', fontWeight:'600', flexShrink:0 }}>
                      🖨️ Print / PDF
                    </button>
                  </div>
                  {/* Report Content */}
                  <div style={{ padding:'20px 24px' }}>
                    {aiText.split('\n').map((line,i)=>{
                      if(line.match(/^[0-9]+\.\s+[A-Z]/)||line.match(/^#{1,3}\s/)||line.match(/^[A-Z][A-Z\s]{4,}:$/))
                        return <h3 key={i} style={{ fontSize:'13px', fontWeight:'800', color:'#1e293b', margin:'14px 0 6px', paddingBottom:'4px', borderBottom:'1px solid #f1f5f9', textTransform:'uppercase', letterSpacing:'.3px' }}>{line.replace(/^#{1,3}\s/,'').replace(/^[0-9]+\.\s/,'')}</h3>;
                      if(line.startsWith('- ')||line.startsWith('• '))
                        return <div key={i} style={{ display:'flex', gap:'8px', margin:'4px 0', fontSize:'13px', color:'#334155', lineHeight:'1.6' }}><span style={{ color:'#2563eb', flexShrink:0 }}>•</span><span>{line.slice(2)}</span></div>;
                      if(line.startsWith('**')||line.match(/^\*\*.*\*\*$/))
                        return <div key={i} style={{ fontWeight:'700', fontSize:'13px', color:'#1e293b', margin:'8px 0 4px' }}>{line.replace(/\*\*/g,'')}</div>;
                      if(line.trim()==='') return <div key={i} style={{ height:'8px' }} />;
                      return <p key={i} style={{ margin:'4px 0', fontSize:'13px', color:'#475569', lineHeight:'1.7' }}>{line}</p>;
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {!aiData && !aiLoading && (
            <div style={{ textAlign:'center', padding:'60px', color:'#94a3b8' }}>
              <div style={{ fontSize:'40px', marginBottom:'12px' }}>🤖</div>
              <div style={{ fontWeight:'600' }}>Click Analyze to generate AI insights</div>
              <div style={{ fontSize:'12px', marginTop:'4px' }}>Compares this week vs last week across all sub-dists</div>
            </div>
          )}
        </div>
      )}

      {/* ADMIN REPORTS TAB */}
      {tab==='reports' && isAdmin && (
        <div>
          <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap', alignItems:'center' }}>
            <FilterBar onFilter={loadReports} showDateRange={true} />
            <select style={{ ...inp, width:'120px' }} value={filterType} onChange={e=>setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option value="olt">OLT</option>
              <option value="switch">Switch</option>
            </select>
          </div>
          {/* Director-level summary stats */}
          {reports.length>0 && (() => {
            const oltReports = reports.filter(r=>r.device_type==='olt');
            const swReports = reports.filter(r=>r.device_type==='switch');
            const subDists = [...new Set(reports.map(r=>r.sub_dist_code).filter(Boolean))];
            const highLoadPons = oltReports.reduce((s,r)=>s+(r.pon_ports||[]).filter(p=>p.active_onts>=80&&p.active_onts<100).length,0);
            const criticalPons = oltReports.reduce((s,r)=>s+(r.pon_ports||[]).filter(p=>p.active_onts>=100).length,0);
            const oltsWithIssues = oltReports.filter(r=>(r.pon_ports||[]).some(p=>p.active_onts>=100)).length;
            const devicesReported = [...new Set(reports.map(r=>r.device_id?._id||r.device_id).filter(Boolean))].length;
            return (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'12px' }}>
                  {[
                    { label:'Sub-Districts Covered', val:subDists.length, color:'#2563eb', bg:'#eff6ff', icon:'🏘️' },
                    { label:'Devices Reported', val:devicesReported, color:'#7c3aed', bg:'#f5f3ff', icon:'📡' },
                    { label:'OLTs with Issues (≥80)', val:oltsWithIssues, color:'#d97706', bg:'#fffbeb', icon:'⚠️' },
                    { label:'Critical PONs (≥100)', val:criticalPons, color:'#dc2626', bg:'#fef2f2', icon:'🔴' },
                  ].map(s=>(
                    <div key={s.label} style={{ background:s.bg, borderRadius:'10px', padding:'14px', textAlign:'center' }}>
                      <div style={{ fontSize:'20px', marginBottom:'4px' }}>{s.icon}</div>
                      <div style={{ fontSize:'24px', fontWeight:'800', color:s.color }}>{s.val}</div>
                      <div style={{ fontSize:'11px', color:'#64748b' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {/* Sub-district breakdown */}
                {subDists.length>0 && (
                  <div style={{ background:'#f8fafc', borderRadius:'10px', padding:'12px 16px', marginBottom:'12px' }}>
                    <div style={{ fontSize:'12px', fontWeight:'700', color:'#475569', marginBottom:'8px' }}>📊 SUB-DISTRICT BREAKDOWN</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
                      {subDists.map(sd=>{
                        const sdOlts = oltReports.filter(r=>r.sub_dist_code===sd);
                        const sdSws = swReports.filter(r=>r.sub_dist_code===sd);
                        const sdCrit = sdOlts.reduce((s,r)=>s+(r.pon_ports||[]).filter(p=>p.active_onts>=100).length,0);
                        return (
                          <div key={sd} style={{ background:'#fff', border:`1px solid ${sdCrit>0?'#fca5a5':'#e2e8f0'}`, borderRadius:'8px', padding:'8px 12px', minWidth:'120px' }}>
                            <div style={{ fontWeight:'700', fontSize:'12px', color:'#1e293b' }}>{sd}</div>
                            <div style={{ fontSize:'11px', color:'#64748b' }}>OLTs: {sdOlts.length} | SW: {sdSws.length}</div>
                            {sdCrit>0 && <div style={{ fontSize:'10px', color:'#dc2626', fontWeight:'700' }}>⚠️ {sdCrit} high PONs</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          {loading ? <div style={{ textAlign:'center', padding:'40px', color:'#94a3b8' }}>Loading...</div> : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
              {[...new Set(reports.map(r=>r.sub_dist_code).filter(Boolean))].sort().map(sd=>{
                const sdReports = reports.filter(r=>r.sub_dist_code===sd);
                const sdOlts = sdReports.filter(r=>r.device_type==='olt');
                const sdSws = sdReports.filter(r=>r.device_type==='switch');
                const sdCrit = sdOlts.reduce((s,r)=>s+(r.pon_ports||[]).filter(p=>p.active_onts>=100).length,0);
                const sdWarn = sdOlts.reduce((s,r)=>s+(r.pon_ports||[]).filter(p=>p.active_onts>=80&&p.active_onts<100).length,0);
                const isExpSd = expandedSubDists[sd];
                const health = sdCrit>0?'🔴':sdWarn>0?'🟡':'✅';
                const borderColor = sdCrit>0?'#fca5a5':sdWarn>0?'#fcd34d':'#e2e8f0';
                const headerBg = sdCrit>0?'#fef2f2':sdWarn>0?'#fffbeb':'#f8fafc';
                return (
                  <div key={sd} style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                    {/* Sub-district header */}
                    <div onClick={()=>toggleSubDist(sd)} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', cursor:'pointer', background:headerBg, userSelect:'none' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                        <span style={{ fontSize:'20px' }}>{health}</span>
                        <div>
                          <div style={{ fontWeight:'800', fontSize:'14px', color:'#1e293b' }}>🏘️ {sd}</div>
                          <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>
                            {sdOlts.length} OLTs • {sdSws.length} Switches • {sdReports.length} reports
                            {sdCrit>0 && <span style={{ color:'#dc2626', fontWeight:'700', marginLeft:'8px' }}>⚠️ {sdCrit} High PONs</span>}
                            {sdWarn>0 && <span style={{ color:'#d97706', fontWeight:'700', marginLeft:'8px' }}>🟡 {sdWarn} Warning PONs</span>}
                          </div>
                        </div>
                      </div>
                      <span style={{ fontSize:'16px', color:'#94a3b8', transition:'transform .2s', display:'inline-block', transform:isExpSd?'rotate(180deg)':'rotate(0deg)' }}>▼</span>
                    </div>
                    {/* Expanded reports */}
                    {isExpSd && (
                      <div style={{ padding:'12px', borderTop:`1px solid ${borderColor}`, background:'#fafafa' }}>
                        {renderReportsTable(sdReports, true)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* SUBMISSION STATUS TAB */}
      {tab==='status' && isAdmin && (
        <div>
          <FilterBar onFilter={loadStatus} showDateRange={true} />
          <div style={{ display:'flex', gap:'12px', marginBottom:'16px' }}>
            <div style={{ background:'#f0fdf4', borderRadius:'8px', padding:'10px 16px', fontSize:'13px', fontWeight:'600', color:'#15803d' }}>
              ✅ Submitted: {status.filter(s=>s.submitted).length}
            </div>
            <div style={{ background:'#fef2f2', borderRadius:'8px', padding:'10px 16px', fontSize:'13px', fontWeight:'600', color:'#dc2626' }}>
              ⏳ Pending: {status.filter(s=>!s.submitted).length}
            </div>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', background:'#fff', borderRadius:'12px', overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.08)' }}>
            <thead><tr style={{ background:'#f8fafc' }}>
              {['Tech Name','Phone','Sub-Dist','Status','Reports This Week'].map(h=>(
                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'600' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {status.map((s,i)=>(
                <tr key={i} style={{ borderTop:'1px solid #f1f5f9', background:s.submitted?'':'#fffbeb' }}>
                  <td style={{ padding:'10px 14px', fontWeight:'600', fontSize:'13px' }}>{s.tech.name}</td>
                  <td style={{ padding:'10px 14px', fontSize:'12px', color:'#64748b' }}>{s.tech.phone}</td>
                  <td style={{ padding:'10px 14px', fontSize:'12px' }}>{s.tech.sub_dist_code}</td>
                  <td style={{ padding:'10px 14px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'700', background:s.submitted?'#dcfce7':'#fef2f2', color:s.submitted?'#15803d':'#dc2626' }}>
                      {s.submitted ? '✅ Submitted' : '⏳ Pending'}
                    </span>
                  </td>
                  <td style={{ padding:'10px 14px', fontSize:'13px', fontWeight:'600', color:'#2563eb' }}>{s.report_count}</td>
                </tr>
              ))}
              {status.length===0 && <tr><td colSpan={5} style={{ textAlign:'center', padding:'30px', color:'#94a3b8' }}>No tech users found</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  function renderReportsTable(data, showTech) {
    if(!data.length) return (
      <div style={{ textAlign:'center', padding:'60px', color:'#94a3b8' }}>
        <div style={{ fontSize:'40px', marginBottom:'12px' }}>📭</div>
        <div style={{ fontWeight:'600' }}>No reports found</div>
        <div style={{ fontSize:'12px', marginTop:'4px' }}>Try adjusting your filters</div>
      </div>
    );
    if(showTech) {
      data = [...data].sort((a,b)=>{
        const aMax = a.device_type==='olt'?Math.max(...(a.pon_ports||[]).map(p=>p.active_onts||0),0):0;
        const bMax = b.device_type==='olt'?Math.max(...(b.pon_ports||[]).map(p=>p.active_onts||0),0):0;
        return bMax-aMax;
      });
    }
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {data.map(r=>{
          const hasHighPon = r.device_type==='olt'&&(r.pon_ports||[]).some(p=>p.active_onts>=100);
          const hasWarnPon = r.device_type==='olt'&&(r.pon_ports||[]).some(p=>p.active_onts>=80&&p.active_onts<100);
          const hasErrPort = r.device_type==='switch'&&(r.error_ports||0)>0;
          const isExpanded = expandedReports[r._id];
          const maxPon = r.device_type==='olt'?Math.max(...(r.pon_ports||[]).map(p=>p.active_onts||0),0):0;
          const health = hasHighPon?'🔴':hasWarnPon?'🟡':hasErrPort?'🟡':'✅';
          const borderColor = hasHighPon?'#fca5a5':hasWarnPon?'#fcd34d':hasErrPort?'#fcd34d':'#e2e8f0';
          const headerBg = hasHighPon?'#fef2f2':hasWarnPon?'#fffbeb':hasErrPort?'#fffbeb':'white';
          return (
          <div key={r._id} style={{ background:'white', borderRadius:'10px', border:`1px solid ${borderColor}`, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            {/* Clickable Header */}
            <div onClick={()=>toggleReport(r._id)}
              style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 16px', cursor:'pointer', background:headerBg, userSelect:'none' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', flex:1 }}>
                {/* Health indicator */}
                <span style={{ fontSize:'18px' }}>{health}</span>
                {/* Device info */}
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                    <span style={{ fontWeight:'700', fontSize:'13px', color:'#1e293b' }}>{r.device_id?.device_name||'Unknown Device'}</span>
                    <span style={{ padding:'1px 7px', borderRadius:'20px', fontSize:'10px', fontWeight:'700', background:r.device_type==='olt'?'#dbeafe':'#dcfce7', color:r.device_type==='olt'?'#1d4ed8':'#15803d' }}>
                      {r.device_type==='olt'?'📡 OLT':'🔀 SW'}
                    </span>
                    {r.device_id?.device_ip && <span style={{ fontSize:'10px', color:'#94a3b8', fontFamily:'monospace' }}>{r.device_id.device_ip}</span>}
                    {r.device_id?.location && <span style={{ fontSize:'10px', color:'#64748b' }}>📍 {r.device_id.location}</span>}
                  </div>
                  <div style={{ display:'flex', gap:'12px', marginTop:'3px', flexWrap:'wrap' }}>
                    <span style={{ fontSize:'11px', color:'#0f172a', fontWeight:'700' }}>👤 {r.submitted_by?.name||'-'}</span>
                    <span style={{ fontSize:'10px', color:'#94a3b8' }}>🏘️ {r.sub_dist_code||'-'}</span>
                    <span style={{ fontSize:'10px', color:'#64748b' }}>📅 {new Date(r.submitted_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</span>
                    <span style={{ fontSize:'10px', color:'#2563eb', fontWeight:'600' }}>Wk {r.week_no}/{r.year}</span>
                  </div>
                </div>
              </div>
              {/* Right side summary */}
              <div style={{ display:'flex', alignItems:'center', gap:'12px', flexShrink:0 }}>
                {r.device_type==='olt' && maxPon>0 && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:'800', color:hasHighPon?'#dc2626':hasWarnPon?'#d97706':'#15803d' }}>{maxPon}</div>
                    <div style={{ fontSize:'9px', color:'#94a3b8' }}>Max PON</div>
                  </div>
                )}
                {r.device_type==='switch' && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:'800', color:hasErrPort?'#dc2626':'#64748b' }}>{r.error_ports||0}</div>
                    <div style={{ fontSize:'9px', color:'#94a3b8' }}>Err Ports</div>
                  </div>
                )}
                <span style={{ fontSize:'16px', color:'#94a3b8', transition:'transform .2s', display:'inline-block', transform:isExpanded?'rotate(180deg)':'rotate(0deg)' }}>▼</span>
              </div>
            </div>

            {/* Expandable Details */}
            {isExpanded && (
              <div style={{ padding:'14px 16px', borderTop:`1px solid ${borderColor}`, background:'#fafafa' }}>
                {r.device_type==='olt' && r.pon_ports?.length>0 && (
                  <div style={{ marginBottom:'12px' }}>
                    <div style={{ fontSize:'11px', fontWeight:'700', color:'#475569', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>PON Port Active ONTs</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(80px,1fr))', gap:'6px' }}>
                      {r.pon_ports.map((p,i)=>(
                        <div key={i} style={{ padding:'8px', borderRadius:'8px', textAlign:'center',
                          background:p.active_onts>=100?'#fef2f2':p.active_onts>=80?'#fffbeb':'#f0fdf4',
                          border:`1px solid ${p.active_onts>=100?'#fca5a5':p.active_onts>=80?'#fcd34d':'#86efac'}` }}>
                          <div style={{ fontSize:'9px', color:'#64748b', marginBottom:'2px' }}>PON {p.pon_port}</div>
                          <div style={{ fontSize:'20px', fontWeight:'800', color:p.active_onts>=100?'#dc2626':p.active_onts>=80?'#d97706':'#15803d' }}>{p.active_onts}</div>
                          <div style={{ fontSize:'9px', color:'#64748b' }}>active</div>
                          {p.active_onts>=100 && <div style={{ fontSize:'9px', color:'#dc2626', fontWeight:'700', marginTop:'2px' }}>⚠️ HIGH</div>}
                          {p.remarks && <div style={{ fontSize:'8px', color:'#94a3b8', marginTop:'2px' }}>{p.remarks}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {r.device_type==='switch' && (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px', marginBottom:'12px' }}>
                    <div style={{ padding:'10px', borderRadius:'8px', background:'#f0fdf4', textAlign:'center' }}>
                      <div style={{ fontSize:'10px', color:'#64748b', marginBottom:'2px' }}>Traffic In</div>
                      <div style={{ fontSize:'20px', fontWeight:'800', color:'#15803d' }}>{r.traffic_in||0}</div>
                      <div style={{ fontSize:'10px', color:'#64748b' }}>Mbps</div>
                    </div>
                    <div style={{ padding:'10px', borderRadius:'8px', background:'#eff6ff', textAlign:'center' }}>
                      <div style={{ fontSize:'10px', color:'#64748b', marginBottom:'2px' }}>Traffic Out</div>
                      <div style={{ fontSize:'20px', fontWeight:'800', color:'#1d4ed8' }}>{r.traffic_out||0}</div>
                      <div style={{ fontSize:'10px', color:'#64748b' }}>Mbps</div>
                    </div>
                    <div style={{ padding:'10px', borderRadius:'8px', background:r.error_ports>0?'#fef2f2':'#f8fafc', textAlign:'center' }}>
                      <div style={{ fontSize:'10px', color:'#64748b', marginBottom:'2px' }}>Error Ports</div>
                      <div style={{ fontSize:'20px', fontWeight:'800', color:r.error_ports>0?'#dc2626':'#64748b' }}>{r.error_ports||0}</div>
                      {r.error_ports>0 && <div style={{ fontSize:'10px', color:'#dc2626', fontWeight:'700' }}>⚠️ Check</div>}
                    </div>
                  </div>
                )}
                {(r.error_details||r.remarks) && (
                  <div style={{ fontSize:'12px', color:'#64748b', background:'white', padding:'8px 12px', borderRadius:'6px', border:'1px solid #e2e8f0' }}>
                    {r.error_details && <div>⚠️ <b>Errors:</b> {r.error_details}</div>}
                    {r.remarks && <div>📝 <b>Remarks:</b> {r.remarks}</div>}
                  </div>
                )}
              </div>
            )}
          </div>
        );})}
      </div>
    );
  }
}
